import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  type OnDestroy,
  type OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import type { OrgUser } from '../../../server/types/org-types';
import { AuthUserService } from '../auth-user.service';

@Component({
  selector: 'app-thread-recipient-editor-new',
  templateUrl: './thread-recipient-editor-new.component.html',
  styleUrls: ['./thread-recipient-editor-new.component.scss'],
  standalone: false,
})
export class ThreadRecipientEditorNewComponent implements OnInit, OnDestroy {
  private authUserService = inject(AuthUserService);

  @Input() orgId: string;
  @Input() userId: string;
  @Input() set submitAttempted(submitAttempted: boolean) {
    this.submitAttemptedValue = submitAttempted;
    this.possiblyShowError();
  }
  @Output() userIdsOutput = new EventEmitter<string[]>();
  @ViewChild('userInput') userInput: ElementRef<HTMLInputElement>;
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  addUserControl = new FormControl('', {
    validators: [Validators.required],
    nonNullable: true,
  });

  selectedUsers: OrgUser[] = [];
  searchUsers: OrgUser[] = [];

  submitAttemptedValue = false;

  subscriptions = new Subscription();

  ngOnInit() {
    const { orgId } = this;

    const sub = this.addUserControl.valueChanges.subscribe((value: string) => {
      if (value.length) {
        const searchUsers = this.authUserService
          .searchUsers({ orgId, value })
          .filter((u) => u._id !== this.userId) // Do not include self
          .filter((u) => !u.archivedAt) // Do not include archived users
          .filter((u) => !this.selectedUsers.find((s) => s._id === u._id)); // Do not include existing users

        this.searchUsers = searchUsers.map((s) => _.cloneDeep(s));
      } else {
        this.searchUsers = [];
      }
    });
    this.subscriptions.add(sub);

    this.addUserControl.addValidators(() => {
      this.possiblyShowError();
      return {};
    });
  }

  remove(value: OrgUser): void {
    const index = this.selectedUsers.indexOf(value);
    if (index !== -1) this.selectedUsers.splice(index, 1);
    this.userIdsOutput.emit(_.cloneDeep(this.selectedUsers.map((u) => u._id)));
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const user: OrgUser = event.option.value;
    this.selectedUsers.push(user);
    this.userInput.nativeElement.value = '';
    this.addUserControl.setValue('');
    this.userIdsOutput.emit(_.cloneDeep(this.selectedUsers.map((u) => u._id)));
  }

  private possiblyShowError() {
    if (this.submitAttemptedValue && this.selectedUsers.length === 0)
      this.addUserControl.setErrors({ incorrect: true });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
